import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import type { Database } from './types';

export interface SupabaseStorageAdapter {
  getItem(key: string): string | null | Promise<string | null>;
  setItem(key: string, value: string): void | Promise<void>;
  removeItem(key: string): void | Promise<void>;
}

export interface CreateSupabaseOptions {
  url: string;
  anonKey: string;
  storage: SupabaseStorageAdapter;
  detectSessionInUrl?: boolean;
  flowType?: 'implicit' | 'pkce';
  persistSession?: boolean;
  autoRefreshToken?: boolean;
  /**
   * Replaces the client's HTTP transport.
   *
   * Runtime-agnostic on purpose: the web app installs a browser-local
   * PostgREST here for its /demo route, and the Expo app passes nothing and
   * gets supabase-js's default. This package stays platform-neutral either
   * way — `fetch` exists in both runtimes.
   */
  fetch?: typeof fetch;
}

export function createSupabase(opts: CreateSupabaseOptions): SupabaseClient<Database> {
  if (!opts.url || !opts.anonKey) {
    throw new Error('createSupabase: url and anonKey are required');
  }
  return createClient<Database>(opts.url, opts.anonKey, {
    auth: {
      storage: opts.storage,
      persistSession: opts.persistSession ?? true,
      autoRefreshToken: opts.autoRefreshToken ?? true,
      detectSessionInUrl: opts.detectSessionInUrl ?? false,
      flowType: opts.flowType ?? 'implicit',
    },
    // Spread conditionally so a caller that passes no fetch gets exactly the
    // client it got before this option existed.
    ...(opts.fetch ? { global: { fetch: opts.fetch } } : {}),
  });
}

export type { SupabaseClient };
